import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Divider,
  Alert,
  Chip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { AuthContext } from '../../../context/AuthContext';
import { 
  obtenerConfiguracionesValidacion, 
  guardarConfiguracionValidacion, 
  eliminarConfiguracionValidacion 
} from '../data/FirestoreCuadrantes';
import './CuadranteConfig.scss';

const CuadranteConfig = ({ onConfiguracionesChange, onConfiguracionSelect, selectedConfig }) => {
  const { currentService } = useContext(AuthContext);
  
  // Estados para las configuraciones
  const [configuraciones, setConfiguraciones] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showNewConfigDialog, setShowNewConfigDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Estado para nueva configuración
  const [nuevaConfig, setNuevaConfig] = useState({
    nombre: '',
    descripcion: '',
    // Horas y convenios
    horasMesConvenio: 160,
    horasExtraMaximas: 20,
    horasMaximasTurno: 12,
    
    // Descansos
    descansoTrasTurnosLargos: { horas: 8, turnoMinimo: 6 },
    descansoMinimoEntreTurnos: 11, // horas
    diasDescansoSemana: 1,
    descansosFinSemanaMes: 2,
    
    // Vacaciones y permisos
    diasVacaciones: 22,
    vacacionesNaturales: false, // false = laborales
    diasAsuntosPropios: 5,
    
    // Turnos nocturnos
    maxJornadasNocturnasConsecutivas: 3,
    
    // Transporte
    minutosMaximosTransporte: 60,
    
    // Advertencias y dietas
    maxMinutosCambioServicio: 30,
    maxKmCambioServicio: 25,
    dietaPorKm: 0.25, // euros por km
    dietaMinima: 15, // euros mínimos
    
    // Configuraciones adicionales
    permitirHorasExtra: true,
    permitirCambiosServicio: true,
    validarTransporte: true
  });

  useEffect(() => {
    // Cargar configuraciones existentes
    if (currentService) {
      cargarConfiguraciones();
    }
  }, [currentService]);

  const cargarConfiguraciones = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const configs = await obtenerConfiguracionesValidacion(currentService);
      
      // Si no hay configuraciones, crear las predeterminadas
      if (configs.length === 0) {
        const configsPredeterminadas = [
          {
            nombre: 'Configuración Estándar',
            descripcion: 'Configuración básica para la mayoría de servicios',
            horasMesConvenio: 160,
            horasExtraMaximas: 20,
            horasMaximasTurno: 12,
            descansoTrasTurnosLargos: { horas: 8, turnoMinimo: 6 },
            descansoMinimoEntreTurnos: 11,
            diasDescansoSemana: 1,
            descansosFinSemanaMes: 2,
            diasVacaciones: 22,
            vacacionesNaturales: false,
            diasAsuntosPropios: 5,
            maxJornadasNocturnasConsecutivas: 3,
            minutosMaximosTransporte: 60,
            maxMinutosCambioServicio: 30,
            maxKmCambioServicio: 25,
            dietaPorKm: 0.25,
            dietaMinima: 15,
            permitirHorasExtra: true,
            permitirCambiosServicio: true,
            validarTransporte: true
          },
          {
            nombre: 'Configuración Intensiva',
            descripcion: 'Para servicios que requieren más flexibilidad',
            horasMesConvenio: 180,
            horasExtraMaximas: 30,
            horasMaximasTurno: 14,
            descansoTrasTurnosLargos: { horas: 6, turnoMinimo: 8 },
            descansoMinimoEntreTurnos: 8,
            diasDescansoSemana: 1,
            descansosFinSemanaMes: 1,
            diasVacaciones: 20,
            vacacionesNaturales: false,
            diasAsuntosPropios: 3,
            maxJornadasNocturnasConsecutivas: 5,
            minutosMaximosTransporte: 90,
            maxMinutosCambioServicio: 45,
            maxKmCambioServicio: 40,
            dietaPorKm: 0.30,
            dietaMinima: 20,
            permitirHorasExtra: true,
            permitirCambiosServicio: true,
            validarTransporte: true
          }
        ];
        
        // Guardar configuraciones predeterminadas
        for (const config of configsPredeterminadas) {
          await guardarConfiguracionValidacion(config, currentService);
        }
        
        // Recargar configuraciones
        const configsGuardadas = await obtenerConfiguracionesValidacion(currentService);
        setConfiguraciones(configsGuardadas);
      } else {
        setConfiguraciones(configs);
      }
      
      if (onConfiguracionesChange) {
        onConfiguracionesChange(configs);
      }
    } catch (error) {
      console.error('Error al cargar configuraciones:', error);
      setError('Error al cargar las configuraciones. Verifica tu conexión.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const configToSave = {
        ...nuevaConfig,
        servicioId: currentService?.id
      };
      
      const configId = await guardarConfiguracionValidacion(configToSave, currentService);
      
      // Recargar configuraciones
      await cargarConfiguraciones();
      
      setShowNewConfigDialog(false);
      setNuevaConfig({
        nombre: '',
        descripcion: '',
        horasMesConvenio: 160,
        horasExtraMaximas: 20,
        horasMaximasTurno: 12,
        descansoTrasTurnosLargos: { horas: 8, turnoMinimo: 6 },
        descansoMinimoEntreTurnos: 11,
        diasDescansoSemana: 1,
        descansosFinSemanaMes: 2,
        diasVacaciones: 22,
        vacacionesNaturales: false,
        diasAsuntosPropios: 5,
        maxJornadasNocturnasConsecutivas: 3,
        minutosMaximosTransporte: 60,
        maxMinutosCambioServicio: 30,
        maxKmCambioServicio: 25,
        dietaPorKm: 0.25,
        dietaMinima: 15,
        permitirHorasExtra: true,
        permitirCambiosServicio: true,
        validarTransporte: true
      });
    } catch (error) {
      console.error('Error guardando configuración:', error);
      setError('Error al guardar la configuración. Verifica tu conexión.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await eliminarConfiguracionValidacion(configId, currentService);
      
      // Recargar configuraciones
      await cargarConfiguraciones();
      
      // Si la configuración eliminada era la seleccionada, limpiar selección
      if (selectedConfig && selectedConfig.id === configId) {
        if (onConfiguracionSelect) {
          onConfiguracionSelect(null);
        }
      }
    } catch (error) {
      console.error('Error al eliminar configuración:', error);
      setError('Error al eliminar la configuración. Verifica tu conexión.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cuadrante-config">
      <Card className="config-card">
        <CardContent>
          <div className="config-header">
            <Typography variant="h6" component="h2">
              Configuración de Validación
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setShowNewConfigDialog(true)}
              disabled={isLoading}
            >
              Nueva Configuración
            </Button>
          </div>

          {error && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          {isLoading && (
            <Box display="flex" justifyContent="center" margin="16px">
              <CircularProgress />
            </Box>
          )}

          {!isLoading && configuraciones.length === 0 && (
            <Alert severity="info">
              No hay configuraciones disponibles. Se crearán configuraciones predeterminadas al cargar.
            </Alert>
          )}

          {!isLoading && configuraciones.length > 0 && (
            <div className="config-list">
              {configuraciones.map((config) => (
                <Card key={config.id} variant="outlined" style={{ marginBottom: '16px' }}>
                  <CardContent>
                    <div className="config-item-header">
                      <div>
                        <Typography variant="h6">{config.nombre}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {config.descripcion}
                        </Typography>
                      </div>
                      <div className="config-actions">
                        <Button
                          variant={selectedConfig?.id === config.id ? "contained" : "outlined"}
                          color="primary"
                          size="small"
                          onClick={() => onConfiguracionSelect && onConfiguracionSelect(config)}
                          disabled={isLoading}
                        >
                          {selectedConfig?.id === config.id ? "Seleccionada" : "Seleccionar"}
                        </Button>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteConfig(config.id)}
                          disabled={isLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </div>
                    
                    {selectedConfig?.id === config.id && (
                      <Box marginTop="16px">
                        <Chip 
                          label="Configuración Activa" 
                          color="success" 
                          variant="outlined"
                          style={{ marginBottom: '8px' }}
                        />
                        <Typography variant="body2" color="textSecondary">
                          Esta configuración se usará para validar los cuadrantes
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para nueva configuración */}
      <Dialog 
        open={showNewConfigDialog} 
        onClose={() => setShowNewConfigDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Nueva Configuración de Cuadrante</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Información Básica</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de la configuración"
                value={nuevaConfig.nombre}
                onChange={(e) => setNuevaConfig({...nuevaConfig, nombre: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descripción"
                value={nuevaConfig.descripcion}
                onChange={(e) => setNuevaConfig({...nuevaConfig, descripcion: e.target.value})}
              />
            </Grid>

            {/* Horas y convenios */}
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" gutterBottom>Horas y Convenios</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Horas por mes (convenio)"
                value={nuevaConfig.horasMesConvenio}
                onChange={(e) => setNuevaConfig({...nuevaConfig, horasMesConvenio: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Horas extra máximas"
                value={nuevaConfig.horasExtraMaximas}
                onChange={(e) => setNuevaConfig({...nuevaConfig, horasExtraMaximas: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Horas máximas por turno"
                value={nuevaConfig.horasMaximasTurno}
                onChange={(e) => setNuevaConfig({...nuevaConfig, horasMaximasTurno: parseInt(e.target.value)})}
              />
            </Grid>

            {/* Descansos */}
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" gutterBottom>Descansos</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Descanso mínimo entre turnos (horas)"
                value={nuevaConfig.descansoMinimoEntreTurnos}
                onChange={(e) => setNuevaConfig({...nuevaConfig, descansoMinimoEntreTurnos: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Días de descanso por semana"
                value={nuevaConfig.diasDescansoSemana}
                onChange={(e) => setNuevaConfig({...nuevaConfig, diasDescansoSemana: parseInt(e.target.value)})}
              />
            </Grid>

            {/* Vacaciones y permisos */}
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" gutterBottom>Vacaciones y Permisos</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Días de vacaciones"
                value={nuevaConfig.diasVacaciones}
                onChange={(e) => setNuevaConfig({...nuevaConfig, diasVacaciones: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={nuevaConfig.vacacionesNaturales}
                    onChange={(e) => setNuevaConfig({...nuevaConfig, vacacionesNaturales: e.target.checked})}
                  />
                }
                label="Vacaciones en días naturales"
              />
            </Grid>

            {/* Transporte y dietas */}
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="h6" gutterBottom>Transporte y Dietas</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Minutos máximos de transporte"
                value={nuevaConfig.minutosMaximosTransporte}
                onChange={(e) => setNuevaConfig({...nuevaConfig, minutosMaximosTransporte: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="KM máximos cambio servicio"
                value={nuevaConfig.maxKmCambioServicio}
                onChange={(e) => setNuevaConfig({...nuevaConfig, maxKmCambioServicio: parseInt(e.target.value)})}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                step="0.01"
                label="Dieta por KM (€)"
                value={nuevaConfig.dietaPorKm}
                onChange={(e) => setNuevaConfig({...nuevaConfig, dietaPorKm: parseFloat(e.target.value)})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewConfigDialog(false)}>Cancelar</Button>
          <Button onClick={handleSaveConfig} variant="contained" color="primary">
            Guardar Configuración
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CuadranteConfig; 